import { ReactNode, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined as DescargarIcon,
  UploadFile as UploadIcon,
  RateReview as RateReviewIcon,
  OpenInNew as VerTemaIcon,
  TuneOutlined as EstadoIcon,
  EditOutlined as EditarIcon,
  AddOutlined as AsignarIcon,
  GroupsOutlined as ComisionIcon,
  KeyboardArrowDown as DesplegarIcon,
} from "@mui/icons-material";
import Correo from "../kit/Correo";
import EstadoChip from "../kit/EstadoChip";
import Markdown from "../kit/Markdown";
import { InstanciaPaso, RolComision } from "../../types/instanciaPaso";

const ROL_COMISION: Record<RolComision, string> = { guia: "Guía", coguia: "Coguía", comision: "Comisión" };
import { useCambiarEstado, usePublicarNota, useSubirDocumento } from "../../queries/useInstancias";
import { ConformarComisionDialog, EvaluarDialog } from "./ComisionEvaluacion";
import TemaProcesoDialog from "./TemaProcesoDialog";
import DefensaSala from "./DefensaSala";
import { formatFechaHora, tiempoRestante } from "../../utils/fecha";
import { estiloDeInstancia, iconoDeInstancia } from "../../utils/estados";
import { mensajeDeError } from "../../utils/errores";
import { useIr } from "../../utils/enlaces";

/**
 * La fecha de entrega al lado del nombre del paso.
 *
 * Es lo primero que se mira de un proceso abierto —cuánto queda— y abajo, entre los
 * demás datos, había que ir a buscarla. Sin plazo fijado lo dice igual: que no haya
 * fecha también es algo que el coordinador necesita ver.
 */
function EntregaInline({ iso }: { iso: string | null }) {
  const restante = tiempoRestante(iso);
  const vencido = restante === "vencido";
  if (!iso) {
    return (
      <Typography component="span" variant="body2" color="text.disabled">
        sin fecha de entrega
      </Typography>
    );
  }
  return (
    <Typography component="span" variant="body2" color="text.secondary">
      Entrega {formatFechaHora(iso)}
      {restante && (
        <Box component="span" sx={{ ml: 0.5, fontWeight: 600, color: vencido ? "error.main" : "text.secondary" }}>
          ({restante})
        </Box>
      )}
    </Typography>
  );
}

/**
 * Documentos (como botones) y la subida/reemplazo, todo en la misma fila.
 *
 * El estudiante sube mientras `puede_subir` —su proceso, en plazo, abierto—. El
 * coordinador puede reponer el archivo de cualquier proceso abierto, también fuera de
 * plazo: llegó el PDF equivocado, o llegó por correo. Cerrado, ya no. Repone el
 * archivo y nada más: ni la nota, ni las evaluaciones, ni la fecha de entrega.
 */
function DocumentosYSubida({ it, gestionCoordinador = false }: { it: InstanciaPaso; gestionCoordinador?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const subir = useSubirDocumento();
  const docs = [
    { url: it.documento, label: "Documento" },
    { url: it.documento_correccion, label: "Corrección" },
  ].filter((d) => d.url);
  // Solo los pasos que llevan documento (el de fecha coordina y agenda, no se entrega
  // nada), y solo abiertos: aprobado o reprobado, se cerró con el documento que tenía.
  const cerrado = it.estado === "aprobado" || it.estado === "reprobado";
  const puedeSubir = (it.puede_subir || gestionCoordinador) && it.paso_lleva_documento && !cerrado;

  if (docs.length === 0 && !puedeSubir) return null;

  return (
    // Más aire arriba y abajo: es la acción principal del proceso y se perdía entre el
    // tema y la comisión.
    <Box sx={{ mt: 2.5, mb: 1 }}>
      <input
        ref={input}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          if (archivo) subir.mutate({ id: it.id, archivo });
          e.target.value = "";
        }}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent="flex-start">
        {docs.map((d) => (
          <Button
            key={d.label}
            size="small"
            variant="outlined"
            startIcon={<DescargarIcon />}
            component="a"
            href={d.url as string}
            target="_blank"
            rel="noreferrer"
          >
            {d.label}
          </Button>
        ))}
        {puedeSubir && (
          <Button
            size="small"
            variant={it.documento ? "text" : "outlined"}
            startIcon={<UploadIcon />}
            disabled={subir.isPending}
            onClick={() => input.current?.click()}
          >
            {subir.isPending ? "Subiendo…" : it.documento ? "Reemplazar documento" : "Subir documento (PDF)"}
          </Button>
        )}
      </Stack>
      {it.documento && it.fecha_documento && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          Documento subido el {formatFechaHora(it.fecha_documento)}
        </Typography>
      )}
      {subir.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {mensajeDeError(subir.error, "No se pudo subir el documento.")}
        </Alert>
      )}
    </Box>
  );
}

/** Una línea de dato: el rótulo a la izquierda y el valor al lado (en el celular, encima). */
function FilaDato({ label, accion, children }: { label: string; accion?: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "110px 1fr" }, columnGap: 1.5, alignItems: "start" }}>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {accion}
      </Stack>
      <Box sx={{ minWidth: 0, typography: "body2" }}>{children}</Box>
    </Box>
  );
}

/** El lápiz que va al lado de un rótulo: edita lo que ese rótulo nombra. */
function Lapiz({ titulo, onClick }: { titulo: string; onClick: () => void }) {
  return (
    <Tooltip title={titulo}>
      <IconButton size="small" onClick={onClick} aria-label={titulo} sx={{ p: 0.25 }}>
        <EditarIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Un profesor del proceso: su nombre completo y su correo para escribirle.
 *
 * Nombre y correo en la misma línea: son el mismo dato —a quién le escribo—. El correo
 * va en el `Correo` del kit, que se copia con un clic.
 */
function Persona({ nombre, email }: { nombre: string; email?: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap component="span">
      <span>{nombre}</span>
      {!!email && <Correo correo={email} />}
    </Stack>
  );
}

type EvaluacionComision = InstanciaPaso["evaluaciones"][number];

/** Lo que respondió un integrante: todas las preguntas, con lo que contestó. */
function Respuestas({ evaluacion }: { evaluacion: EvaluacionComision }) {
  return (
    <Stack spacing={0.75} sx={{ borderLeft: "2px solid", borderColor: "divider", pl: 1.5, mt: 0.75, mb: 1 }}>
      {evaluacion.respuestas.map((r, j) => (
        <Box key={j}>
          <Typography variant="caption" color="text.secondary" display="block">
            {r.pregunta}
          </Typography>
          {/* Los comentarios se escriben en Markdown (ver `EditorMarkdown`); lo demás, tal cual. */}
          {r.tipo === "texto" ? (
            <Markdown texto={String(r.valor)} />
          ) : (
            <Typography variant="body2">{String(r.valor)}</Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}

/**
 * Un integrante de la comisión y, plegada debajo, su evaluación.
 *
 * El botón para desplegarla aparece solo si hay algo que mostrar: quien no ha
 * respondido no lo tiene. Plegadas por defecto: con tres o cuatro profesores y sus
 * comentarios largos, abiertas todas la tarjeta se hace interminable.
 */
function FilaIntegrante({
  titulo,
  evaluacion,
  inactiva = false,
}: {
  titulo: ReactNode;
  evaluacion?: EvaluacionComision;
  /** Ya no integra la comisión: se ve atenuada y el tooltip dice por qué. */
  inactiva?: boolean;
}) {
  const [abierta, setAbierta] = useState(false);
  const hayRespuestas = !!evaluacion && evaluacion.respuestas.length > 0;
  return (
    <Box
      component="li"
      title={inactiva ? "Ya no integra la comisión: su evaluación no cuenta para el resultado." : undefined}
      sx={inactiva ? { opacity: 0.45 } : undefined}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ typography: "body2" }}>
        {titulo}
        {hayRespuestas && (
          <Button
            size="small"
            variant="text"
            onClick={() => setAbierta((a) => !a)}
            endIcon={<DesplegarIcon sx={{ transform: abierta ? "rotate(180deg)" : "none", transition: "transform .15s" }} />}
            sx={{ py: 0 }}
          >
            {abierta ? "Ocultar evaluación" : "Ver evaluación"}
          </Button>
        )}
      </Stack>
      {hayRespuestas && (
        <Collapse in={abierta} unmountOnExit>
          <Respuestas evaluacion={evaluacion} />
        </Collapse>
      )}
    </Box>
  );
}

/**
 * La comisión: el resultado, cada integrante con su evaluación plegada, y publicar.
 *
 * Las evaluaciones que el usuario puede ver ya vienen filtradas del backend (el
 * estudiante, recién al publicarse). Las de quien salió de la comisión después de
 * evaluar —solo las ve quien gestiona— van al final, marcadas: se guardan pero no pesan.
 */
function Comision({
  it,
  gestionCoordinador,
  resultado,
  children,
}: {
  it: InstanciaPaso;
  gestionCoordinador: boolean;
  resultado: string | null;
  children?: ReactNode;
}) {
  const finalizado = it.estado === "aprobado" || it.estado === "reprobado";
  const deCada = new Map(it.evaluaciones.map((e) => [e.evaluador_id, e]));
  const miembros = new Set(it.comision.map((m) => m.id));
  const fuera = it.evaluaciones.filter((e) => !miembros.has(e.evaluador_id) && e.respuestas.length > 0);
  const vacia = it.comision.length === 0 && fuera.length === 0 && !resultado;

  return (
    <Box sx={{ mt: 1.5 }}>
      {/* Separa la comisión de lo de arriba (el documento): son dos momentos distintos. */}
      <Divider sx={{ mb: 1.5 }} />
      {/* Sin comisión todavía, solo el botón que la conforma: un rótulo sobre nada y una
          etiqueta que lo repite no agregan nada que el botón no diga. */}
      <Stack direction="row" alignItems="center" spacing={0.25}>
        {!vacia && (
          <Typography variant="caption" color="text.secondary">
            Comisión
          </Typography>
        )}
        {children}
      </Stack>
      {resultado && (
        <Box sx={{ mt: 0.5 }}>
          <FilaDato label="Resultado">{resultado}</FilaDato>
        </Box>
      )}
      {(it.comision.length > 0 || fuera.length > 0) && (
        <Stack component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }} spacing={0.25}>
          {it.comision.map((m) => (
            <FilaIntegrante
              key={m.id}
              evaluacion={deCada.get(m.id)}
              titulo={
                <>
                  <span>{m.nombre}</span>
                  <Typography component="span" variant="caption" color="text.secondary">
                    · {ROL_COMISION[m.rol]}
                  </Typography>
                  {/* Avance de evaluación (para el coordinador): quién ya evaluó y quién falta. */}
                  {gestionCoordinador && !finalizado && (
                    <Typography component="span" variant="caption" sx={{ color: m.evaluado ? "success.main" : "warning.main" }}>
                      · {m.evaluado ? "✓ evaluó" : "pendiente"}
                    </Typography>
                  )}
                  {!gestionCoordinador && !!m.email && <Correo correo={m.email} />}
                </>
              }
            />
          ))}
          {/* Quien salió de la comisión después de evaluar: atenuado, como algo que
              ya no pesa. Su evaluación se guarda y se puede leer, pero no cuenta. */}
          {fuera.map((e) => (
            <FilaIntegrante key={`fuera-${e.evaluador_id}`} evaluacion={e} inactiva titulo={<span>{e.evaluador}</span>} />
          ))}
        </Stack>
      )}

      {/* Coordinador: qué daría publicar ahora, y el botón. Sigue estando después de
          cerrar: si alguien corrigió su evaluación o el que faltaba por fin respondió,
          lo publicado dejó de ser cierto y hay que volver a publicar. */}
      {gestionCoordinador && it.comision.length > 0 && <Publicar it={it} />}
    </Box>
  );
}

/** Los estados a los que el coordinador puede mover un proceso a mano. */
const ESTADOS: { valor: string; label: string; ayuda: string }[] = [
  { valor: "pendiente", label: "Pendiente", ayuda: "Como si no hubiera entregado." },
  { valor: "entregado", label: "Entregado", ayuda: "Entregó y todavía no tiene comisión." },
  { valor: "en_evaluacion", label: "En evaluación", ayuda: "Vuelve a manos de la comisión." },
  { valor: "observacion", label: "Observación", ayuda: "Algo quedó por resolver; la evaluación se suspende." },
  { valor: "aprobado", label: "Aprobado", ayuda: "Cierra el proceso sin pasar por el formulario." },
  { valor: "reprobado", label: "Reprobado", ayuda: "Cierra el proceso sin pasar por el formulario." },
  { valor: "eliminado", label: "Dado de baja", ayuda: "Sale de todas las listas; la fila se conserva." },
];

/** Los estados propios del paso de fecha (`estado_paso`), en tonos del kit. */
const ESTADO_PASO: Record<"no_coordinado" | "coordinado" | "agendado", { tono: "aviso" | "info" | "exito"; contorno?: boolean }> = {
  no_coordinado: { tono: "aviso" },
  coordinado: { tono: "info", contorno: true },
  agendado: { tono: "exito" },
};

/** Los estados en los que un proceso todavía no tiene resultado guardado. */
const SIN_RESULTADO = ["pendiente", "entregado", "en_evaluacion"];

/** Los tres desenlaces que se eligen al publicar. */
const DESENLACES: { valor: string; label: string; ayuda: string }[] = [
  { valor: "aprobado", label: "Aprobar", ayuda: "Cierra el proceso con el resultado." },
  {
    valor: "observacion",
    label: "Dejar en observación",
    ayuda: "Queda algo por resolver. El estudiante no ve la nota, sí los comentarios.",
  },
  { valor: "reprobado", label: "Reprobar", ayuda: "Cierra el proceso con el resultado." },
];

/**
 * Corregir a mano el estado de un proceso (coordinador).
 *
 * El estado lo derivan la entrega y la publicación, y casi siempre basta con eso. Esto
 * es para cuando no: el caso que lo pide es **devolver a evaluación** algo ya publicado
 * —se mira la nota, no cuadra, y la comisión tiene que verlo de nuevo— y después
 * volver a publicar.
 *
 * Volver a un estado sin resultado borra la nota publicada, y el diálogo lo dice: no
 * se pierde —sale de las evaluaciones, que siguen ahí— pero el estudiante deja de
 * verla hasta que se publique otra vez.
 */
function CambiarEstado({ it }: { it: InstanciaPaso }) {
  const cambiar = useCambiarEstado();
  const [abierto, setAbierto] = useState(false);
  const [estado, setEstado] = useState(it.estado);

  const abrir = () => {
    setEstado(it.estado);
    setAbierto(true);
  };
  const elegido = ESTADOS.find((e) => e.valor === estado);
  const pierdeNota = SIN_RESULTADO.includes(estado) && (it.nota !== null || !!it.resultado_categoria);

  return (
    <>
      <Tooltip title="Cambiar el estado del proceso">
        <IconButton size="small" onClick={abrir} aria-label="Cambiar el estado del proceso">
          <EstadoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={abierto} onClose={() => setAbierto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Estado del proceso</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            El estado sale solo de la entrega y de la publicación. Cámbialo a mano cuando haga falta corregirlo.
          </DialogContentText>
          <TextField select fullWidth size="small" label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
            {ESTADOS.map((e) => (
              <MenuItem key={e.valor} value={e.valor}>
                {e.label}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  · {e.ayuda}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          {pierdeNota && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Se borra el resultado publicado ({it.resultado_categoria || it.nota}). Queda en las evaluaciones y vuelve
              al publicar de nuevo.
            </Alert>
          )}
          {cambiar.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {mensajeDeError(cambiar.error, "No se pudo cambiar el estado.")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbierto(false)} disabled={cambiar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={cambiar.isPending || estado === it.estado}
            onClick={() => cambiar.mutate({ id: it.id, estado }, { onSuccess: () => setAbierto(false) })}
          >
            {cambiar.isPending ? "Guardando…" : `Dejar como ${elegido?.label.toLowerCase() ?? ""}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Publicar el resultado, en sus tres formas, para el coordinador.
 *
 * - **Publicar**: la comisión completó y el proceso sigue abierto. Es el caso normal.
 * - **Publicar anticipadamente**: falta gente por evaluar y se decide no esperarla. Va
 *   con confirmación y dice quién falta: publicar cierra el proceso y le manda la nota
 *   al estudiante, así que no es algo para apretar de pasada.
 * - **Volver a publicar**: el proceso ya está cerrado. Hace falta porque las
 *   evaluaciones siguen cambiando después —alguien corrige la suya, o el que faltaba
 *   por fin responde— y lo publicado se queda viejo sin forma de arreglarlo. Cuando lo
 *   que daría ahora difiere de lo publicado, se dice en pantalla.
 *
 * El correo al estudiante lo decide el backend: sale solo si el resultado cambia.
 */
function Publicar({ it }: { it: InstanciaPaso }) {
  const publicar = usePublicarNota();
  const [confirmar, setConfirmar] = useState(false);
  const [desenlace, setDesenlace] = useState<string>("");
  const cerrado = ["aprobado", "reprobado", "observacion"].includes(it.estado);
  const faltan = it.comision.filter((m) => !m.evaluado).map((m) => m.nombre);
  const ahora = it.resultado_provisional;
  const publicado = it.resultado_categoria || (it.nota ?? "");
  // Lo que daría publicar ahora contra lo que el estudiante está viendo.
  const cambio = cerrado && !!ahora && (ahora.etiqueta || ahora.nota) !== String(publicado);
  // El desenlace propuesto lo decide el backend, que es donde están los umbrales.
  const elegido = desenlace || ahora?.sugerido || "aprobado";

  const abrir = () => {
    setDesenlace(ahora?.sugerido ?? "aprobado");
    setConfirmar(true);
  };
  const enviar = () =>
    publicar.mutate(
      { id: it.id, anticipada: faltan.length > 0, resultado: elegido },
      { onSuccess: () => setConfirmar(false) },
    );
  const etiqueta = cerrado ? "Volver a publicar" : faltan.length ? "Publicar anticipadamente" : "Publicar resultado";

  return (
    // A la derecha, como cierre de la comisión: lo que daría publicar y el botón juntos.
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1.5} flexWrap="wrap" useFlexGap>
        {!ahora ? (
          <Typography variant="body2" color="text.disabled">
            La comisión aún no evalúa.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {cerrado ? "Con lo evaluado hoy daría" : "Resultado provisional"}:{" "}
            <Box component="span" sx={{ color: cambio ? "warning.main" : "text.primary", fontWeight: 700, fontSize: 16 }}>
              {ahora.etiqueta || ahora.nota}
            </Box>
            {cambio && ` · publicado: ${publicado}`}
          </Typography>
        )}
        <Button
          size="small"
          variant={cambio || !cerrado ? "contained" : "outlined"}
          disabled={!it.puede_publicar || publicar.isPending}
          onClick={abrir}
          // La flecha anuncia que publicar no es un clic y listo: hay que elegir el
          // desenlace (aprobar, observación o reprobar).
          endIcon={<DesplegarIcon />}
        >
          {publicar.isPending ? "Publicando…" : etiqueta}
        </Button>
      </Stack>
      {publicar.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {mensajeDeError(publicar.error, "No se pudo publicar la nota.")}
        </Alert>
      )}

      <Dialog open={confirmar} onClose={() => setConfirmar(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{etiqueta}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            {ahora && (
              <Box sx={{ mb: 1 }}>
                La comisión da <strong>{ahora.etiqueta || ahora.nota}</strong>
                {cambio && (
                  <>
                    , y hoy está publicado <strong>{publicado}</strong>
                  </>
                )}
                .
              </Box>
            )}
            {faltan.length > 0 && (
              <Box>
                Todavía no evalúan: <strong>{faltan.join(", ")}</strong>. Se publica igual, con lo que hay.
              </Box>
            )}
          </DialogContentText>
          {/* La nota no decide sola: con el mismo número se puede aprobar, reprobar o
              dejar algo pendiente de resolver. La propuesta viene del backend. */}
          <TextField select fullWidth size="small" label="Qué se hace" value={elegido} onChange={(e) => setDesenlace(e.target.value)}>
            {DESENLACES.map((d) => (
              <MenuItem key={d.valor} value={d.valor}>
                {d.label}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  · {d.ayuda}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          {ahora && !ahora.aprueba && elegido === "aprobado" && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Con {ahora.etiqueta || ahora.nota} el proceso no alcanza la nota de aprobación.
            </Alert>
          )}
          {ahora && ahora.aprueba && elegido === "reprobado" && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Con {ahora.etiqueta || ahora.nota} el proceso sí alcanza la nota de aprobación.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            {elegido === "observacion"
              ? "El estudiante no ve la nota, sí los comentarios de la comisión, y recibe un aviso de que su proceso quedó en observación."
              : cerrado
                ? "Al estudiante se le avisa por correo solo si el resultado cambia."
                : "El proceso se cierra y al estudiante le llega su resultado por correo."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(false)} disabled={publicar.isPending}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={enviar} disabled={publicar.isPending}>
            {publicar.isPending ? "Publicando…" : DESENLACES.find((d) => d.valor === elegido)?.label}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Tarjeta de un proceso (InstanciaPaso). Reutilizada en Procesos y en la ficha. */
export default function ProcesoCard({
  it,
  showEstudiante = true,
  gestionCoordinador = false,
  mostrarPeriodo = true,
}: {
  it: InstanciaPaso;
  showEstudiante?: boolean;
  gestionCoordinador?: boolean;
  /** false donde las tarjetas ya van bajo un título con el año y el semestre. */
  mostrarPeriodo?: boolean;
}) {
  const [evaluar, setEvaluar] = useState(false);
  const [editarTema, setEditarTema] = useState(false);
  const [editarComision, setEditarComision] = useState(false);
  const ir = useIr();
  const finalizado = it.estado === "aprobado" || it.estado === "reprobado";
  // Revocar, asignar y cambiar el tema solo tienen sentido sobre un proceso en curso:
  // en uno cerrado el tema es historia y no se toca. Van junto al tema porque es lo que
  // le pasa al tema. El tema puede no venir del catálogo (`tema_id`): basta el título.
  const gestionaTema = gestionCoordinador && !finalizado;
  // Los correos son para que el estudiante sepa a quién escribirle. En la ficha que
  // gestiona la coordinación solo llenaban la tarjeta.
  const mostrarCorreos = !gestionCoordinador;
  const lapizTema = gestionaTema && it.tema ? <Lapiz titulo="Cambiar tema" onClick={() => setEditarTema(true)} /> : undefined;
  const puedeEditarComision = gestionCoordinador && !finalizado && it.paso_se_evalua;
  const datosEstudiante = showEstudiante && !!it.estudiante;
  // La etiqueta cuando el resultado sale de una pregunta de opción ("Aprobado con
  // comentarios"); si no, la nota a secas. Sin resultado todavía, no se muestra.
  const resultado = it.paso_se_evalua ? it.resultado_categoria || it.nota : null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            {/* El plazo pegado al nombre del paso: juntos dicen de una qué toca y para
                cuándo. Es para quien entrega: en la ficha que gestiona la coordinación no va. */}
            <Stack direction="row" alignItems="baseline" spacing={1.25} flexWrap="wrap" useFlexGap>
              <Typography variant="h6">{it.paso}</Typography>
              {!gestionCoordinador && <EntregaInline iso={it.fecha_entrega} />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {[it.curso, it.tipo_display, mostrarPeriodo && `${it.ano} · ${it.semestre_display}`].filter(Boolean).join("  ·  ")}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {/* Un tipo de paso con estado propio (el de fecha: no coordinado, coordinado,
                agendado) lo muestra en vez del estado del proceso. */}
            {it.estado_paso ? (
              <EstadoChip label={it.estado_paso.label} {...ESTADO_PASO[it.estado_paso.codigo]} denso />
            ) : (
              <EstadoChip label={it.estado_display} {...estiloDeInstancia(it.estado)} icono={iconoDeInstancia(it.estado)} denso />
            )}
            {gestionCoordinador && <CambiarEstado it={it} />}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* 1. El tema: tema, guía y coguía, uno por línea. El lápiz al lado de cualquiera
            de los tres abre el mismo diálogo, que los edita juntos (y revoca). Lo que no
            hay no se muestra —un rótulo con un guion no dice nada—; sin tema queda el
            botón que lo pone. */}
        {(datosEstudiante || it.tema || it.guia || it.coguia || gestionaTema) && (
          <Stack spacing={0.75}>
            {datosEstudiante && <FilaDato label="Estudiante">{it.estudiante}</FilaDato>}
            {it.tema ? (
              <FilaDato label="Tema" accion={lapizTema}>
                {it.tema}
                {it.tema_id && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<VerTemaIcon />}
                    onClick={(e) => ir(e, `/app/temas/${it.tema_id}`)}
                    sx={{ ml: 0.5, py: 0 }}
                  >
                    Ver original
                  </Button>
                )}
              </FilaDato>
            ) : (
              // Sin tema, solo el botón que lo pone: el rótulo y una etiqueta que lo repita
              // no dicen nada que el botón no diga.
              gestionaTema && (
                <Box>
                  <Button size="small" variant="outlined" startIcon={<AsignarIcon />} onClick={() => setEditarTema(true)}>
                    Asignar tema
                  </Button>
                </Box>
              )
            )}
            {it.guia && (
              <FilaDato label="Guía" accion={lapizTema}>
                <Persona nombre={it.guia} email={mostrarCorreos ? it.guia_email : undefined} />
              </FilaDato>
            )}
            {it.coguia && (
              <FilaDato label="Coguía" accion={lapizTema}>
                <Persona nombre={it.coguia} email={mostrarCorreos ? it.coguia_email : undefined} />
              </FilaDato>
            )}
          </Stack>
        )}

        {/* 2. El documento: verlo y subirlo o reemplazarlo. */}
        <DocumentosYSubida it={it} gestionCoordinador={gestionCoordinador} />

        {/* Paso de fecha: la coordinación de los integrantes y la sala en SAR. La agendan
            el estudiante o la coordinación (desde la ficha); al estudiante se le avisa cuando
            la hace la coordinación. */}
        <DefensaSala it={it} coordinacion={gestionCoordinador} />

        {/* 3. La comisión, y bajo cada integrante su evaluación, plegada. En un paso que
            no se evalúa (el de fecha) no va: sus integrantes se ven en la coordinación. */}
        {it.paso_se_evalua && (it.comision.length > 0 || puedeEditarComision || !!resultado) && (
          <Comision it={it} gestionCoordinador={gestionCoordinador} resultado={resultado}>
            {/* Con comisión, un lápiz, igual que el tema; sin comisión, el botón con texto. */}
            {puedeEditarComision &&
              (it.comision.length ? (
                <Lapiz titulo="Editar comisión" onClick={() => setEditarComision(true)} />
              ) : (
                <Button size="small" variant="text" startIcon={<ComisionIcon />} onClick={() => setEditarComision(true)}>
                  Conformar comisión
                </Button>
              ))}
          </Comision>
        )}

        {it.soy_comision && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Button size="small" variant="text" startIcon={<RateReviewIcon />} onClick={() => setEvaluar(true)}>
              Evaluar
            </Button>
          </Stack>
        )}
      </CardContent>

      {evaluar && <EvaluarDialog it={it} onClose={() => setEvaluar(false)} />}
      {editarTema && <TemaProcesoDialog it={it} onClose={() => setEditarTema(false)} />}
      {editarComision && <ConformarComisionDialog it={it} onClose={() => setEditarComision(false)} />}
    </Card>
  );
}
